// apiService.ts

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface ApiResponse {
  status: number;
  data: any;
}

export interface ApiError {
  status: number;
  message: string;
}

export async function makeApiCall(
  url: string,
  method: HttpMethod,
  data?: any,
  options?: any,
  signal?: AbortSignal
): Promise<ApiResponse> {
  const { isPublicRoute } = options || {};

  const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;
  const token = tokenKey ? localStorage.getItem(tokenKey) : null;
  let body;
  let headers: HeadersInit = {};

  if (data instanceof FormData) {
    body = data;
  } else if (data) {
    body = JSON.stringify(data);
    headers["Content-Type"] = "application/json";
  }

  if (!isPublicRoute) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestOptions: RequestInit = {
    method,
    headers,
    body: ["POST", "PUT"].includes(method) ? body : undefined,
    signal,
  };

  if (
    (method === "GET" || method === "DELETE") &&
    data &&
    typeof data === "object"
  ) {
    const queryString = Object.entries(data)
      .filter(([_, value]) => !!value || typeof value === "boolean")
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    url = `${url}?${queryString}`;
  }

  try {
    const response = await fetch(url, requestOptions);
    const contentType = response.headers.get("content-type");

    let responseData: any;

    if (contentType?.includes("application/json")) {
      responseData = await response.json();
    } else if (contentType?.includes("application/pdf")) {
      responseData = await response.blob();
    } else {
      responseData = await response.text();
    }

    let message;
    if (!response.ok) {
      switch (response.status.toString()) {
        case "400":
          message =
            "Bad Request: The server could not understand the request due to invalid syntax.";
          let err = ""; // Initialize error message container

          // Check if responseData is an object
          let res =
            typeof responseData === "string"
              ? JSON.parse(responseData)
              : responseData;

          if (res?.detail) {
            try {
              let parsedDetail = JSON.parse(res.detail);

              if (parsedDetail?.errors) {
                Object.keys(parsedDetail.errors).forEach((key) => {
                  parsedDetail.errors[key].forEach((error: string) => {
                    err += `${error}\n`;
                  });
                });
              }
            } catch (e) {
              console.error("Error parsing detail field:", e);
            }
          }

          message = err ? err : message;
          break;

        case "401":
          message =
            "Unauthorized: Authentication is required and has failed or has not yet been provided.";
          break;
        case "403":
          message = "Forbidden: you do not have access rights to the content";
          break;
        case "404":
          message =
            "Not Found: The server can not find the requested resource.";
          break;
        case "500":
          message =
            "Internal Server Error: The server encountered an unexpected condition that prevented it from fulfilling the request.";
          break;
        case "502":
          message =
            "Bad Gateway: The server, while acting as a gateway or proxy, received an invalid response from the upstream server.";
          break;

        default:
          message =
            responseData.Detail || "An error occurred. Please try again later.";
      }
      return { data: message, status: response.status };
    }
    return { data: responseData, status: response.status };
  } catch (error) {
    console.error("Error during API call:", error);
    throw new Error("Failed to make API call");
  }
}
